import React, { useEffect, useState } from "react";
import "./Sidebar.css";
import { UilBars } from "@iconscout/react-unicons";
import { DepartmentData } from "../../Data/Data";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { userRequest } from "../../RequestMethods";
import { getSeenAlertIds, LOW_STOCK_ALERTS_SEEN_EVENT } from "../../utils/lowStockAlertsSeen";
import { getSeenShortfallOrderIds, SHORTFALL_ORDERS_SEEN_EVENT } from "../../utils/shortfallOrdersSeen";

const LOW_STOCK_ALERTS_POLL_MS = 60000;
const SHORTFALL_ORDERS_POLL_MS = 60000;

const Sidebar = () => {
  const loginState = useSelector((state) => state.auth);
  const location = useLocation();
  const [expanded, setExpanded] = useState(true);
  const [lowStockAlertCount, setLowStockAlertCount] = useState(0);
  const [shortfallOrderCount, setShortfallOrderCount] = useState(0);

  const currentUser = loginState.currentUser;
  const isAdmin = currentUser?.worker?.isAdmin === true;
  const userPermissions = currentUser?.permissions || [];
  const canSeeStockAlerts = isAdmin || userPermissions.includes("stock:read");
  const canSeeOrders = isAdmin || userPermissions.includes("orders:read");

  useEffect(() => {
    if (!currentUser || !canSeeStockAlerts) {
      setLowStockAlertCount(0);
      return undefined;
    }

    let cancelled = false;

    const fetchLowStockAlertCount = async () => {
      try {
        const res = await userRequest.get("stock/alerts", { params: { status: "open" } });
        if (cancelled) return;

        const openAlerts = res.data?.alerts || [];
        const seenIds = await getSeenAlertIds();
        if (cancelled) return;
        const unseenCount = openAlerts.filter((alert) => !seenIds.has(alert._id)).length;

        setLowStockAlertCount(unseenCount);
      } catch (err) {
        console.error("Sidebar: failed to fetch low stock alert count:", err);
      }
    };

    fetchLowStockAlertCount();
    const interval = setInterval(fetchLowStockAlertCount, LOW_STOCK_ALERTS_POLL_MS);

    // Refresh instantly when the alerts page marks alerts as seen, instead
    // of waiting for the next poll tick or a route change.
    window.addEventListener(LOW_STOCK_ALERTS_SEEN_EVENT, fetchLowStockAlertCount);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener(LOW_STOCK_ALERTS_SEEN_EVENT, fetchLowStockAlertCount);
    };
  }, [currentUser, canSeeStockAlerts]);

  useEffect(() => {
    if (!currentUser || !canSeeOrders) {
      setShortfallOrderCount(0);
      return undefined;
    }

    let cancelled = false;

    const fetchShortfallOrderCount = async () => {
      try {
        const res = await userRequest.get("orders", { params: { hasShortfall: "true" } });
        if (cancelled) return;

        const shortfallOrders = res.data?.orders || [];
        const seenIds = await getSeenShortfallOrderIds();
        if (cancelled) return;
        const unseenCount = shortfallOrders.filter((order) => !seenIds.has(order._id)).length;

        setShortfallOrderCount(unseenCount);
      } catch (err) {
        // 404 just means "no shortfall orders" (getAllOrders' empty-result shape) - not a real error
        if (err.response?.status !== 404) {
          console.error("Sidebar: failed to fetch shortfall order count:", err);
        }
        if (!cancelled) setShortfallOrderCount(0);
      }
    };

    fetchShortfallOrderCount();
    const interval = setInterval(fetchShortfallOrderCount, SHORTFALL_ORDERS_POLL_MS);

    window.addEventListener(SHORTFALL_ORDERS_SEEN_EVENT, fetchShortfallOrderCount);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener(SHORTFALL_ORDERS_SEEN_EVENT, fetchShortfallOrderCount);
    };
  }, [currentUser, canSeeOrders]);

  const canSeeItem = (item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.permission && !isAdmin && !userPermissions.includes(item.permission)) return false;
    return true;
  };

  const canSeeDept = (dept) => {
    if (dept.adminOnly && !isAdmin) return false;
    if (dept.to) return true;
    return (dept.items || []).some(canSeeItem);
  };

  const isActive = (to) =>
    to === "/"
      ? location.pathname === "/"
      : location.pathname === to || location.pathname.startsWith(to + "/");

  const sidebarVariants = {
    true: { left: "0" },
    false: { left: "-60%" },
  };

  if (!currentUser) return null;

  return (
    <>
      <div
        className="bars"
        style={expanded ? { left: "60%" } : { left: "5%" }}
        onClick={() => setExpanded(!expanded)}
      >
        <UilBars />
      </div>

      <motion.div
        className="sidebar"
        variants={sidebarVariants}
        animate={window.innerWidth <= 768 ? `${expanded}` : ""}
      >
        <div className="logo">
          <div className="logo-mark">
            <svg viewBox="0 0 24 24" fill="none" stroke="#0B2A5C" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 17V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10" />
              <path d="M4 17h16" />
              <path d="M9 12h6" />
            </svg>
          </div>
          <div className="logo-text">
            <span className="logo-brand">Outex</span>
            <span className="logo-erp">ERP</span>
          </div>
        </div>

        <nav className="menu">
          {DepartmentData.filter(canSeeDept).map((dept) => {
            // Direct link (Dashboard)
            if (dept.to) {
              return (
                <Link
                  key={dept.label}
                  to={dept.to}
                  className={`menuItem${isActive(dept.to) ? " active" : ""}`}
                >
                  <dept.icon size={18} />
                  <span>{dept.label}</span>
                </Link>
              );
            }

            const visibleItems = (dept.items || []).filter(canSeeItem);
            if (!visibleItems.length) return null;

            return (
              <div key={dept.label} className="dept-section">
                <p className="dept-label">{dept.label}</p>
                {visibleItems.map((item) => {
                  const visibleChildren = (item.children || []).filter(canSeeItem);
                  const hasChildren = visibleChildren.length > 0;

                  if (!hasChildren) {
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`menuItem${isActive(item.to) ? " active" : ""}`}
                      >
                        <item.icon size={18} />
                        <span>{item.heading}</span>
                        {item.to === "/orders" && shortfallOrderCount > 0 && (
                          <span className="menuItem-sub-badge">{shortfallOrderCount}</span>
                        )}
                      </Link>
                    );
                  }

                  // On a sub-page (e.g. "Historique des mouvements") the selection
                  // moves to that sub-item; the parent only stays selected on its
                  // own page and on pages that have no sub-item of their own.
                  const activeChild = visibleChildren.some((child) => isActive(child.to));
                  const parentSelected = isActive(item.to) && !activeChild;

                  return (
                    <div key={item.to} className="menu-group">
                      <Link
                        to={item.to}
                        className={`menuItem${parentSelected ? " active" : ""}${activeChild ? " has-active-child" : ""}`}
                      >
                        <item.icon size={18} />
                        <span>{item.heading}</span>
                      </Link>

                      {visibleChildren.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={`menuItem--sub${isActive(child.to) ? " active" : ""}`}
                        >
                          <span className="menuItem-sub-dot" />
                          <span>{child.heading}</span>
                          {child.to === "/stock/alerts" && lowStockAlertCount > 0 && (
                            <span className="menuItem-sub-badge">{lowStockAlertCount}</span>
                          )}
                        </Link>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </motion.div>
    </>
  );
};

export default Sidebar;
